import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BookOpen,
  Award,
  Clock,
  Terminal,
  SlidersHorizontal,
  HardDrive,
  ShieldCheck,
  Cloud,
  Database,
  ChevronRight,
  type LucideIcon
} from 'lucide-react'
import { SectionGrid } from '../SectionGrid'
import { coursesAPI } from '../../../api/courses'
import { exercisesAPI } from '../../../api/exercises'
import { calcDurationMinutes, formatDuration } from '../../../utils/duration'
import type { LabModule } from '../../../types/api'

const MODULE_ICONS: LucideIcon[] = [BookOpen, SlidersHorizontal, Database, HardDrive, ShieldCheck, Cloud, Award]
const getModuleIcon = (index: number): LucideIcon => MODULE_ICONS[index % MODULE_ICONS.length]

interface LabModuleWithProgress extends LabModule {
  status?: string
  completed_exercises?: number
  total_exercises?: number
}

interface ModuleCardProps {
  module: LabModuleWithProgress
  icon: LucideIcon
  index: number
  exerciseCount: number
  onClick: () => void
}

const ModuleCard = ({ module, icon: Icon, index, exerciseCount, onClick }: ModuleCardProps): React.JSX.Element => {
  const progress = module.progress ?? 0
  const isCompleted = progress >= 100
  const isEmpty = exerciseCount === 0
  const moduleLabel = `M${module.order_index != null ? module.order_index - 1 : index}`

  return (
    <div
      className={`group bg-white rounded-xl border border-gray-200 border-l-4 flex flex-col overflow-hidden transition-all ${isEmpty ? 'opacity-60 cursor-not-allowed border-l-gray-300' : 'border-l-[#7a6020] hover:-translate-y-1'}`}
      style={{ transition: 'box-shadow 0.2s, transform 0.2s' }}
      onMouseEnter={(e) => { if (!isEmpty) (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 30px rgba(34, 34, 38, 0.20)' }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = 'none' }}
    >
      <div className="p-4 flex flex-col gap-2.5 flex-1">
        <div className="flex items-center justify-between">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: isEmpty ? '#f3f4f6' : '#f5ede0' }}
          >
            <Icon className="w-5 h-5" style={{ color: isEmpty ? '#9ca3af' : '#7a6020' }} />
          </div>
          <span
            className="text-xs font-semibold px-2.5 py-1 rounded-lg"
            style={isEmpty ? { background: '#f3f4f6', color: '#9ca3af' } : { background: '#f5ede0', color: '#7a6020' }}
          >
            {moduleLabel}
          </span>
        </div>

        <h3 className="text-base font-bold text-gray-900 leading-snug">{module.title}</h3>

        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
          {module.description || 'Explore comprehensive topics and hands-on exercises in this module.'}
        </p>

        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Clock className="w-3.5 h-3.5" />
          <span>{isEmpty ? '—' : formatDuration(calcDurationMinutes(exerciseCount))}</span>
          <span className="mx-1">•</span>
          <Terminal className="w-3.5 h-3.5" />
          <span>{isEmpty ? 'No labs yet' : `${exerciseCount} Labs`}</span>
        </div>

        {!isEmpty && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Progress</span>
              <span className="font-semibold text-gray-700">{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${progress}%`, background: '#7a6020' }}
              />
            </div>
          </div>
        )}

        {isEmpty ? (
          <button
            disabled
            className="mt-1 w-full flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-lg border border-gray-200 text-sm font-medium text-gray-400 bg-gray-50 cursor-not-allowed"
          >
            No Labs in This Module
          </button>
        ) : isCompleted ? (
          <button
            onClick={onClick}
            className="mt-1 w-full flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Review Lab <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={onClick}
            className="mt-1 w-full flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-lg text-sm font-semibold text-white transition-colors hover:opacity-90"
            style={{ background: '#7a6020' }}
          >
            Continue Learning <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

interface AvailableModulesProps {
  courseId: string | undefined
  onModulesLoaded?: (modules: LabModuleWithProgress[]) => void
  onTotalDurationMinutes?: (minutes: number) => void
}

export const AvailableModules: React.FC<AvailableModulesProps> = ({
  courseId,
  onModulesLoaded,
  onTotalDurationMinutes
}) => {
  const navigate = useNavigate()
  const [modules, setModules] = useState<LabModuleWithProgress[]>([])
  const [exerciseCounts, setExerciseCounts] = useState<Record<number, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!courseId) {
        setLoading(false)
        return
      }
      try {
        const data = await coursesAPI.getCourseModules(courseId)
        setModules(data)
        onModulesLoaded?.(data)

        const counts = await Promise.all(
          data.map((m: LabModuleWithProgress) =>
            exercisesAPI
              .getModuleExercises(String(m.id))
              .then((ex: unknown[]) => ({ id: m.id, count: ex.length }))
              .catch(() => ({ id: m.id, count: 0 }))
          )
        )
        const countMap: Record<number, number> = {}
        let total = 0
        counts.forEach(({ id, count }) => {
          countMap[id] = count
          total += count
        })
        setExerciseCounts(countMap)
        onTotalDurationMinutes?.(calcDurationMinutes(total))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [courseId])

  return (
    <SectionGrid
      title="Available Modules"
      subtitle="Follow the structured path to complete your certification."
      loading={loading}
      error={error}
      emptyText="This course doesn't have any lab modules yet."
      loadingText="Loading modules..."
      gridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
    >
      {modules.map((module, idx) => (
        <ModuleCard
          key={module.id}
          module={module}
          icon={getModuleIcon(idx)}
          index={idx}
          exerciseCount={exerciseCounts[module.id] ?? 0}
          onClick={() => navigate(`/course/${courseId}/module/${module.id}/exercises`)}
        />
      ))}
    </SectionGrid>
  )
}
